import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useRequest } from "../../hooks/useRequest";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/useAppContext";
import { priceSymbol } from "../../utils/symbol";

const CartOrderSummaryStyle = styled.div`
  width: 380px;
  border-radius: 11.25px;
  border: 0.75px solid #ccc2c2;
  background: #fff;
  margin-top: 35px;
  .cart__summary__header {
    padding: 22px 22px 24px;
    border-bottom: 0.75px solid #ccc2c2;
  }
  .cart__summary__header {
    .cart__summary__title {
      color: #000;
      font-size: 22.5px;
      font-weight: 600;
      margin-bottom: 32px;
    }
  }
  .cart__summary__subtotal,
  .cart__summary__total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .subtotal__title {
      color: #000;
      font-size: 16.5px;
      font-weight: 500;
    }
    .subtotal__value {
      color: #ae0000;
      font-size: 18px;
      font-weight: 600;
      line-height: 22.5px;
    }
  }
  .cart__summary__coupon {
    .cart_rewards_container {
      display: flex;
      align-items: center;
      justify-content: space-between;

      .apply_rewards {
        background: #ae0000;
        color: white;
        border: none;
        border-radius: 10px;
        padding: 8px 16px;
        cursor: pointer;
      }
    }
  }
  .cart__summary__subtitle {
    color: #808082;
    font-size: 13.5px;
    font-weight: 400;
    margin-top: 12px;
  }
  .cart__summary__coupon__title,
  .cart__summart__total__title {
    color: #000;
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 17.5px;
  }
  .cart__summary__total__wrapper {
    padding: 27px 22px 24px;
    .cart__summary__total {
      &.other {
        .subtotal__title {
          font-size: 12px;
        }
        .subtotal__value {
          font-size: 14px;
        }
      }
      &:nth-last-child(2) {
        margin-bottom: 32px;
        margin-top: 12px;
      }
    }
  }
  .cart__summary__buttons {
    padding: 30px 22px;
    button {
      font-size: 15px;
      line-height: 22.5px;
      border-radius: 5px;
      height: 58px;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
    }
    .continue__shopping {
      color: #000;
      font-weight: 500;
      border: 0.5px solid #ccc2c2;
      background: #fff;
    }
    .checkout {
      font-weight: 700;
      margin-top: 12px;
      color: #fff;
      background: #ae0000;
      &:disabled {
        background: #a7a7a7;
      }
    }
  }
  .vnb_remove {
    color: #ae0000;
    display: block;
    margin-top: 5px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  @media (max-width: 768px) {
    width: 100%;
    .cart__summary__header,
    .cart__summary__coupon,
    .cart__summary__total__wrapper {
      padding: 15px;
      .cart__summary__title {
        font-size: 16px;
        margin-bottom: 20px;
      }
    }
    .cart__summary__coupon__title {
      font-size: 14px;
      position: relative;
    }
    .cart__summary__subtotal,
    .cart__summary__total {
      .subtotal__title,
      .subtotal__value {
        font-size: 14px;
      }
    }
    .cart__summary__total__wrapper {
      .cart__summary__total {
        margin-bottom: 20px;
      }
    }
    .cart__summary__buttons {
      padding: 20px;
    }
  }
`;

const CartOrderSummary = ({ isCheckoutButtonDisabled, isQtyChanged, cartData }) => {
  const [orderSummary, { isLoading: isOrderSummaryLoading }] = useRequest("/order/checkout/summary");
  const [orderSummaryData, setOrderSummaryData] = useState();
  const navigate = useNavigate();
  const { setCheckoutCartData, appliedReward, setAppliedReward } = useAppContext();
  const cartId = cartData?._id;

  const handleRewards = (reward) => {
    setAppliedReward(reward);
    cartId && getOrderSummary(cartId, "", reward);
  };

  async function getOrderSummary(cartId, coupon, reward) {
    const path = `/order/checkout/summary`;
    const response = await orderSummary({
      path: path,
      method: "POST",
      body: JSON.stringify({
        cartId,
        usedReward: reward ?? appliedReward ?? 0,
      }),
    });
    if (response.success) {
      setOrderSummaryData(response?.data);
    }
  }

  useEffect(() => {
    if (cartId) {
      getOrderSummary(cartId, "");
    }
  }, [cartId, isQtyChanged, orderSummaryData?.discountAmount]);

  const handleCheckout = async () => {
    // For seller-wise carts, we need to ensure this cart is active before checkout
    if (cartData && !cartData.isActive) {
      // Switch to this cart first
      try {
        const response = await orderSummary({
          path: `/cart/switch/${cartData._id}`,
          method: "POST"
        });
        if (response.success) {
          // Cart is now active, proceed with checkout
          setCheckoutCartData(cartData);
          navigate("/checkout");
        }
      } catch (error) {
        console.error('Error switching cart:', error);
      }
    } else {
      setCheckoutCartData(cartData);
      navigate("/checkout");
    }
  };

  return (
    <CartOrderSummaryStyle>
      <div className="cart__summary__header">
        <div className="cart__summary__title">Order Summary</div>
        <div className="cart__summary__subtotal">
          <div className="subtotal__title">Subtotal</div>
          <div className="subtotal__value">
            {priceSymbol}
            {orderSummaryData?.subtotal.toFixed(2)}
          </div>
        </div>
      </div>
      {orderSummaryData?.usedReward === 0 && orderSummaryData?.availableReward > 0 && (
        <div className="cart__summary__coupon">
          <div className="cart__summary__coupon__title">IB Rewards</div>
          <div className="cart_rewards_container">
            <span>
              Available :{" "}
              <span style={{ fontWeight: 600, color: "#ae0000" }}>${orderSummaryData?.availableReward.toFixed(2)}</span>
            </span>
            <div className="cart__summary__coupon__apply">
              <button
                disabled={isOrderSummaryLoading}
                onClick={() => handleRewards(orderSummaryData?.availableReward)}
                className="apply_rewards"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="cart__summary__total__wrapper">
        {orderSummaryData?.discountAmount ? (
          <div className="cart__summary__total other">
            <div className="subtotal__title">
              Applied Coupon Discount
            </div>
            <div className="subtotal__value" style={{ color: "#4caf50" }}>
              {priceSymbol}
              {orderSummaryData?.discountAmount.toFixed(2)}
            </div>
          </div>
        ) : (
          ""
        )}
        <div className="cart__summary__total other">
          <div className="subtotal__title">Shipping</div>
          <div className="subtotal__value">
            {priceSymbol}
            {orderSummaryData?.shippingCharges.toFixed(2)}
          </div>
        </div>
        <i style={{ fontSize: "12px", color: "#211f1f", fontWeight: 500, margin: "10px 0", display: "block" }}>
          Shipping Fee will be confirmed after Order Confirmation
        </i>
        <div className="cart__summary__total other">
          <div className="subtotal__title">GST Tax</div>
          <div className="subtotal__value">
            {priceSymbol}
            {orderSummaryData?.gst.toFixed(2)}
          </div>
        </div>
        <div className="cart__summary__total other">
          <div className="subtotal__title">PST Tax</div>
          <div className="subtotal__value">
            {priceSymbol}
            {orderSummaryData?.pst.toFixed(2)}
          </div>
        </div>
        {orderSummaryData?.usedReward > 0 && (
          <div className="cart__summary__total other">
            <div className="subtotal__title">
              IB Rewards
              <span className="vnb_remove" onClick={() => handleRewards(0)}>
                Remove
              </span>
            </div>
            <div className="subtotal__value" style={{ color: "#4caf50" }}>
              {priceSymbol}
              {orderSummaryData?.usedReward > 0 ? orderSummaryData?.usedReward.toFixed(2) : 0}
            </div>
          </div>
        )}
        <div className="cart__summary__total">
          <div className="subtotal__title">Total</div>
          <div className="subtotal__value">
            {priceSymbol}
            {orderSummaryData?.totalAmount.toFixed(2)}
          </div>
        </div>

        <p className="cart__summary__subtitle">Tax included and shipping calculated at checkout</p>
      </div>
      <div className="cart__summary__buttons">
        <button className="continue__shopping" onClick={() => navigate("/")}>
          Continue Shopping
        </button>
        <button
          className="checkout"
          disabled={
            isOrderSummaryLoading ||
            isCheckoutButtonDisabled ||
            orderSummaryData?.lowerTheQuantity ||
            orderSummaryData?.outOfStock
          }
          onClick={handleCheckout}
        >
          {orderSummaryData?.outOfStock
            ? "Out of Stock"
            : orderSummaryData?.lowerTheQuantity
            ? "Lower product quantity"
            : "Checkout"}
        </button>
      </div>
    </CartOrderSummaryStyle>
  );
};

export default CartOrderSummary;
